// src/components/PurchasesAndCart.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from './public/PublicLayout';

const PurchasesAndCart = ({ user }) => {
  const [activeTab, setActiveTab] = useState('purchases');
  const [purchases, setPurchases] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchasesAndCart();
  }, []);

  const fetchPurchasesAndCart = async () => {
    try {
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch purchases and cart data
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const [purchasesResponse, cartResponse] = await Promise.all([
        fetch(`${API_BASE}/store/orders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/store/cart`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const purchasesData = purchasesResponse.ok ? (await purchasesResponse.json()).data.orders || [] : [];
      const cartData = cartResponse.ok ? (await cartResponse.json()).data.cart?.items || [] : [];

      setPurchases(purchasesData);
      setCartItems(cartData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching purchases and cart:', error);
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      if (!token) return;

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/store/cart/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setCartItems(cartItems.filter(item => item.id !== itemId));
      }
    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-4 text-muted">Loading your purchases and cart...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="bg-light py-4">
        <div className="container">
          <div className="mb-4">
            <h1 className="h3 fw-bold text-dark">My Purchases & Cart</h1>
            <p className="text-muted">Manage your store purchases and shopping cart</p>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'purchases' ? 'active' : ''}`}
                onClick={() => setActiveTab('purchases')}
              >
                My Purchases
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'cart' ? 'active' : ''}`}
                onClick={() => setActiveTab('cart')}
              >
                Shopping Cart
              </button>
            </li>
          </ul>

          {/* Purchases Tab */}
          {activeTab === 'purchases' && (
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                {purchases.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Product</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchases.map((purchase) => (
                          <tr key={purchase.id}>
                            <td>#{purchase.id?.substring(0, 8)}</td>
                            <td>{purchase.items?.[0]?.product_name || 'Order Items'}</td>
                            <td>{purchase.created_at ? new Date(purchase.created_at).toLocaleDateString() : 'N/A'}</td>
                            <td>${purchase.total_amount || purchase.items?.reduce((sum, item) => sum + (item.total_price || 0), 0)}</td>
                            <td>
                              <span className={`badge bg-${purchase.status === 'completed' ? 'success' : 'warning'}`}>
                                {purchase.status || 'Processing'}
                              </span>
                            </td>
                            <td>
                              <button className="btn btn-sm btn-outline-primary">View Details</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">You haven't made any purchases yet.</p>
                    <Link to="/store" className="btn btn-primary">Browse Store</Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cart Tab */}
          {activeTab === 'cart' && (
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                {cartItems.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Price</th>
                            <th>Quantity</th>
                            <th>Total</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartItems.map((item) => (
                            <tr key={item.id}>
                              <td>{item.product_name || 'Product'}</td>
                              <td>${item.unit_price || 0}</td>
                              <td>{item.quantity || 1}</td>
                              <td>${(item.unit_price * item.quantity || 0).toFixed(2)}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeFromCart(item.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <h4 className="mb-0">
                        Total: ${cartItems.reduce((sum, item) => sum + (item.unit_price * item.quantity || 0), 0).toFixed(2)}
                      </h4>
                      <div>
                        <button className="btn btn-secondary me-2">Continue Shopping</button>
                        <button className="btn btn-success">Proceed to Checkout</button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Your shopping cart is empty.</p>
                    <Link to="/store" className="btn btn-primary">Browse Store</Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default PurchasesAndCart;